import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { PatientService } from "../../../../core/services/patient.service";
import { LogHelper } from "../../../../core/helpers/log.helper";
import { AlertService } from "../../../../shared/alert/alert.service";
import { ChangeLog } from "../../../../shared/change-log/change-log.model";
import { ModalComponent } from "../../../../shared/modal/modal.component";
import { AuthService } from "../../../../core/services/auth.service";
import { RoleNames } from "../../../../core/constants/role-names";
import { Permissions } from "../../../../core/constants/permissions";

@Component({
  selector: 'app-patient-detail-change-log',
  templateUrl: './patient-detail-change-log.component.html'
})
export class PatientDetailChangeLogComponent implements OnInit {
  // Export
  @ViewChild('dateFrom') dateFrom: ElementRef;
  @ViewChild('dateTo') dateTo: ElementRef;
  @ViewChild('exportChangeLogModal') exportChangeLogModal: ModalComponent;
  exportIsProcessing = false;
  exportForm: UntypedFormGroup;
  dataLoaded: boolean = false;

  @Input() patientTrialId: string;
  changeLogResults: ChangeLog;

  constructor(private readonly patientService: PatientService, private readonly alertService: AlertService, protected readonly authService: AuthService) { }

  ngOnInit() {
    // Export sites form
    this.exportForm = new UntypedFormGroup({
      dateFrom: new FormControl<Date|null>(null, [Validators.required]),
      dateTo: new FormControl<Date|null>(null, [Validators.required])
    }, { validators: this.dateRangeValidator });

    this.loadChangeLog();
  }

  private dateRangeValidator(control: AbstractControl): ValidationErrors | null {
    const dateFrom = control.get('dateFrom')?.value as Date | null;
    const dateTo = control.get('dateTo')?.value as Date | null;

    if (!dateFrom || !dateTo) {
      return null;
    }

    if (dateTo < dateFrom) {
      return { dateRange: true };
    }

    return null;
  }

  loadChangeLog(page = 1) {
    this.dataLoaded = false;
    this.patientService.changeLog(this.patientTrialId, page).subscribe({
      next: results => {
        this.changeLogResults = results;
        this.dataLoaded = true;
      },
      error: () => {
        this.dataLoaded = true;
        this.alertService.showWarningAlert('There was a problem loading the change log.');
      }
    });
  }

  onPageChanged(page: number) {
    this.loadChangeLog(page);
  }

  /**
   * Updates the angular form when you mouse over the export button, it pulls the dates from the fields and puts them
   * into the form. It's a bodge to fix an issue with the jQuery datepicker and angular
   */
  updateExportFormValues() {
    this.exportForm.patchValue({
      dateFrom: this.dateFrom.nativeElement.value,
      dateTo: this.dateTo.nativeElement.value
    });
  }

  onExportChangeLog() {
    this.exportIsProcessing = true;
    const from = this.exportForm.get('dateFrom').value;
    const to = this.exportForm.get('dateTo').value;

    this.patientService.exportChangeLog(this.patientTrialId, from, to).subscribe({
      next: () => {
        this.alertService.showSuccessAlert('The export request was sent, please check your email.');
        this.exportChangeLogModal.hide();
        this.exportIsProcessing = false;
        this.exportForm.patchValue({
          dateFrom: '',
          dateTo: ''
        });
      },
      error: error => {
        LogHelper.log(error);
        this.exportIsProcessing = false;
        this.alertService.showWarningAlert('There was a problem requesting the export.');
      }
    });
  }

  protected readonly RoleNames = RoleNames;
  protected readonly Permissions = Permissions;
}
